import React from 'react'

import { CustomSpineEvents } from '../../../types/shared'
import { useModelState } from '../../model-hooks/use-model-state'
import ChartModel from '../../models/chart'
import ChartLayout from '../../models/chart_layout'
import { AutoSaveState } from '../../models/constants'
import Element from '../../models/element'
import { usePrevious } from '../../utils/use-prev-state'
import { DRAWINGS_UPDATE_INTERVAL_MS } from './constants'
import { useDrawingAutoSaveControls } from './use-drawing-auto-save-controls'

function useDrawingAutoSave(chartLayout: ChartLayout) {
  const {
    onElementChange,
    deleteAllAutoSavedElements,
    checkDrawingUpdate,
    syncChartLayoutDrawings,
    saveLatestChanges,
    isAutoSaveActive,
  } = useDrawingAutoSaveControls(chartLayout)

  const drawingsInternalStore = useModelState(
    React.useMemo(() => chartLayout.drawingsInternalStore(), [chartLayout]),
    { watchProperties: ['elements'] }
  )
  const prevIsAutoSaveActive = usePrevious(isAutoSaveActive)

  React.useEffect(() => {
    saveLatestChanges()
  }, [saveLatestChanges, drawingsInternalStore.elements])

  React.useEffect(() => {
    if (!isAutoSaveActive) {
      chartLayout.drawingsInternalStore().setAutoSaveState(AutoSaveState.Off)
      return
    }
    chartLayout.drawingsInternalStore().setAutoSaveState(AutoSaveState.Saved)
    if (!prevIsAutoSaveActive && isAutoSaveActive) {
      checkDrawingUpdate(true)
    }

    let tickers: string[] = chartLayout.getAllCharts().map((chart) => chart.getQuoteRawTicker() ?? '')

    const onChartChange = () => {
      const newTickers = chartLayout.getAllCharts().map((chart) => chart.getQuoteRawTicker() ?? '')
      if (JSON.stringify(tickers) !== JSON.stringify(newTickers)) {
        tickers = newTickers
        checkDrawingUpdate(true)
      }
    }
    const onIndicatorChange = () => {
      checkDrawingUpdate(true)
    }

    ChartModel.bind('change', onChartChange)
    ChartModel.bind(CustomSpineEvents.IndicatorsChange, onIndicatorChange)
    Element.bind('change', onElementChange)

    chartLayout.getAllElements().forEach((element) => {
      // Adds drawings to internal store if isAutoSaveActive and they are not in the store yet
      if (!drawingsInternalStore.elements.some((drawing) => drawing.elementId === element.elementId)) {
        onElementChange(element)
      }
    })

    drawingsInternalStore.elements.forEach(syncChartLayoutDrawings)
    const checkDrawingUpdateInterval = setInterval(checkDrawingUpdate, DRAWINGS_UPDATE_INTERVAL_MS)

    return () => {
      clearInterval(checkDrawingUpdateInterval)
      ChartModel.unbind('change', onChartChange)
      ChartModel.unbind(CustomSpineEvents.IndicatorsChange, onIndicatorChange)
      Element.unbind('change', onElementChange)
    }
  }, [
    chartLayout,
    drawingsInternalStore,
    checkDrawingUpdate,
    onElementChange,
    syncChartLayoutDrawings,
    isAutoSaveActive,
    prevIsAutoSaveActive,
  ])

  return { deleteAllAutoSavedElements, isAutoSaveActive }
}

interface Props {
  chartLayoutModel: ChartLayout
}

export const withDrawingsAutoSave = (Component: any) => (props: Props) => {
  const { deleteAllAutoSavedElements, isAutoSaveActive } = useDrawingAutoSave(props.chartLayoutModel)

  return (
    <Component {...props} deleteAllAutoSavedElements={deleteAllAutoSavedElements} isAutoSaveActive={isAutoSaveActive} />
  )
}
